<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { labsApi, labAnalysisTypesApi, type Lab } from '@/services/api/emsl';
import { useAppDialog } from '@/composables/useAppDialog';
import { useAppConfirm } from '@/composables/confirm';
import { useNotifier } from '@/composables/notifier';
import LabDialog from '@/components/dialogs/LabDialog.vue';
import LabMethodsDialog from '@/components/dialogs/LabMethodsDialog.vue';

const labs = ref<Lab[]>([]);
const dialog = useAppDialog();
const confirm = useAppConfirm();
const notify = useNotifier();
const query = ref('');

/**
 * Categories present in the payload. Derived rather than hard-coded so a new
 * analysis category appears without a front-end change — labs#index zero-fills
 * every category, so any lab's keys are the full set.
 */
const categories = computed(() => {
  const keys = new Set<string>();
  for (const lab of labs.value) {
    for (const key of Object.keys(lab.analysis_type_counts ?? {})) keys.add(key);
  }
  return [...keys].sort();
});

/**
 * Word initials: ASBESTOS -> A, INDUSTRIAL HYGIENE -> IH, MICRO -> M. One rule
 * rather than a lookup table, so a category added later gets a sensible short
 * form on its own. Collisions get progressively more letters of the first word,
 * which is unlikely with today's five but cheap to guard.
 */
const abbreviations = computed<Record<string, string>>(() => {
  const initials = (name: string) =>
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0]!)
      .join('')
      .toUpperCase();

  const out: Record<string, string> = {};
  const taken = new Set<string>();
  for (const name of categories.value) {
    let short = initials(name);
    let extra = 1;
    while (taken.has(short) && extra < name.length) short = name.slice(0, ++extra).toUpperCase();
    taken.add(short);
    out[name] = short;
  }
  return out;
});

function count(lab: Lab, category: string): number {
  return lab.analysis_type_counts?.[category] ?? 0;
}

function totalMethods(lab: Lab): number {
  return Object.values(lab.analysis_type_counts ?? {}).reduce((a, b) => a + b, 0);
}

/** A lab with no methods at all cannot be returned to a client for anything. */
function hasNoMethods(lab: Lab): boolean {
  // Absent counts mean "unknown", not "none" — don't flag every lab on an
  // older payload.
  if (!lab.analysis_type_counts) return false;
  return totalMethods(lab) === 0;
}


const visibleLabs = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return labs.value;
  return labs.value.filter((l) =>
    [l.name, l.emsl_lab_id, l.city, l.state, l.email]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q)),
  );
});

function cityLine(lab: Lab): string {
  const left = [lab.city, lab.state].filter(Boolean).join(', ');
  return [left, lab.zip].filter(Boolean).join(' ');
}

async function reload(): Promise<void> {
  const result = await labsApi.index();
  labs.value = Array.isArray(result) ? result : result.data;
}

async function openAdd(): Promise<void> {
  const { result } = dialog.open<Partial<Lab>>(LabDialog, { header: 'Add Lab' });
  const data = await result;
  if (!data) return;
  try {
    await labsApi.create(data);
    notify.success('Lab added');
  } catch {
    notify.error('Lab could not be added');
  }
  await reload();
}

async function openEdit(lab: Lab): Promise<void> {
  const { result } = dialog.open<Partial<Lab>>(LabDialog, {
    header: 'Edit Lab',
    data: { item: lab },
  });
  const data = await result;
  if (!data) return;
  try {
    await labsApi.update({ ...data, id: lab.id });
    notify.success('Lab updated');
  } catch {
    notify.error('Lab could not be updated');
  }
  await reload();
}

/**
 * Test methods are a separate dialog and a separate save: N creates and M
 * deletes, where a partial failure is possible. Reload afterwards so the counts
 * reflect what actually landed rather than what was requested.
 */
async function openMethods(lab: Lab): Promise<void> {
  const { result } = dialog.open<{ analysis_type_ids: number[] }>(LabMethodsDialog, {
    header: `Test Methods — ${lab.name}`,
    width: '64rem',
    data: { item: lab },
  });
  const data = await result;
  if (!data) return;
  try {
    await labAnalysisTypesApi.sync(lab.id, data.analysis_type_ids);
    notify.success('Test methods updated');
  } catch {
    notify.error('Test methods could not be updated');
  }
  await reload();
}

async function remove(lab: Lab): Promise<void> {
  const ok = await confirm({ header: 'Remove Lab', message: `Remove "${lab.name}"?` });
  if (!ok) return;
  try {
    await labsApi.remove(lab.id);
    notify.success('Lab removed');
  } catch {
    notify.error('Lab could not be removed');
  }
  await reload();
}

onMounted(reload);
</script>

<template>
  <section class="labs">
    <header class="labs__header">
      <h1>Labs</h1>
      <div class="labs__header-tools">
        <input
          v-model="query"
          type="search"
          class="labs__search"
          placeholder="Filter labs…"
          aria-label="Filter labs"
        />
        <button type="button" class="labs__cta" @click="openAdd">+ Add Lab</button>
      </div>
    </header>

    <!-- A grid list rather than a table. The table layout algorithm was
         squeezing columns to one character per line when narrow and letting
         the chips overflow under the buttons when wide; grid tracks with
         explicit minimums cannot do either. role=* keeps the semantics a
         table gave for free. -->
    <div class="labs__scroll">
      <div class="labs__list" role="table" aria-label="Labs">
        <div class="labs__row labs__row--head" role="row">
          <span role="columnheader">Lab</span>
          <span role="columnheader">Address</span>
          <span role="columnheader">Contact</span>
          <span role="columnheader">
            Test Methods
            <span class="labs__legend">
              <template v-for="(short, name) in abbreviations" :key="name">
                <span class="labs__legend-item">{{ short }} = {{ name.toLowerCase() }}</span>
              </template>
            </span>
          </span>
          <span role="columnheader"><span class="labs__sr">Actions</span></span>
        </div>

        <div
          v-for="lab in visibleLabs"
          :key="lab.id"
          class="labs__row"
          :class="{ 'labs__row--empty': hasNoMethods(lab) }"
          role="row"
        >
          <span class="labs__cell" role="cell">
            <span class="labs__name">{{ lab.name }}</span>
            <span v-if="lab.emsl_lab_id" class="labs__sub">EMSL ID {{ lab.emsl_lab_id }}</span>
          </span>

          <span class="labs__cell labs__address" role="cell">
            <span v-if="lab.address_1">{{ lab.address_1 }}</span>
            <span v-if="lab.address_2" class="labs__sub">{{ lab.address_2 }}</span>
            <span v-if="cityLine(lab)" class="labs__sub">{{ cityLine(lab) }}</span>
          </span>

          <span class="labs__cell labs__contact" role="cell">
            <span v-if="lab.telephone">{{ lab.telephone }}</span>
            <a v-if="lab.email" class="labs__sub labs__email" :href="`mailto:${lab.email}`">
              {{ lab.email }}
            </a>
          </span>

          <span class="labs__cell" role="cell">
            <span
              class="labs__chips"
              :style="{ gridTemplateColumns: `repeat(${categories.length}, minmax(2.9rem, 1fr))` }"
            >
              <!-- Each chip opens the methods dialog, so the listing is where a
                   gap gets fixed rather than only where it is reported. -->
              <button
                v-for="c in categories"
                :key="c"
                type="button"
                class="labs__chip"
                :class="{ 'labs__chip--zero': count(lab, c) === 0 }"
                :title="`${c}: ${count(lab, c)} — click to edit`"
                @click="openMethods(lab)"
              >
                <span class="labs__chip-key">{{ abbreviations[c] }}</span>
                <span class="labs__chip-n">{{ count(lab, c) }}</span>
              </button>
            </span>
          </span>

          <span class="labs__cell resource__actions" role="cell">
            <button type="button" @click="openEdit(lab)">Edit</button>
            <button type="button" @click="openMethods(lab)">Methods</button>
            <button type="button" @click="remove(lab)">Remove</button>
          </span>
        </div>
      </div>
    </div>

    <p v-if="labs.length === 0" class="labs__empty">No labs.</p>
    <p v-else-if="visibleLabs.length === 0" class="labs__empty">No labs match “{{ query }}”.</p>
  </section>
</template>

<style scoped src="@/assets/styles/resource-list.scss"></style>
<style scoped>
.labs__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
.labs__header-tools {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.labs__search {
  padding: 0.45rem 0.6rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font: inherit;
  font-size: 0.875rem;
  min-width: 14rem;
}
.labs__cta {
  padding: 0.5rem 1rem;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font: inherit;
  white-space: nowrap;
}

/* The list scrolls inside its own box, so the column header can stick to the
   top of that box with `top: 0`. Sticking to the viewport instead would mean
   hard-coding an offset for the app masthead, which is sticky at top: 0 and
   content-sized. The toolbar above stays put because the page itself barely
   scrolls. */
.labs__scroll {
  max-height: calc(100vh - 15rem);
  overflow: auto;
  border-top: 1px solid #e5e7eb;
}
.labs__list {
  display: flex;
  flex-direction: column;
  font-size: 0.875rem;
}

/* Explicit minimums on the flexible tracks and max-content on the two fixed
   ones. That is what stops both failure modes: the chips and buttons always
   get the room they need, and the text columns cannot be squeezed to one
   character per line. */
.labs__row {
  display: grid;
  grid-template-columns:
    minmax(9rem, 1fr)
    minmax(11rem, 1.3fr)
    minmax(10rem, 1.1fr)
    max-content
    max-content;
  gap: 0 1.25rem;
  align-items: start;
  padding: 0.6rem 0.25rem;
  border-bottom: 1px solid #e5e7eb;
}
.labs__row:not(.labs__row--head):hover {
  background: #f9fafb;
}
.labs__row--head {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #4b5563;
  border-bottom: 2px solid #e5e7eb;
  align-items: end;
}
.labs__cell {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}
.labs__name {
  font-weight: 600;
  color: #111827;
}
.labs__sub {
  color: #6b7280;
  font-size: 0.8125rem;
}
/* Only the email needs to break mid-token; applying this everywhere is what
   let the narrow layout wrap names one letter per line. */
.labs__email {
  color: #6b7280;
  text-decoration: none;
  overflow-wrap: anywhere;
}
.labs__email:hover {
  text-decoration: underline;
}
.labs__row--empty .labs__name::after {
  content: ' ⚠';
  color: #b45309;
}

/* Abbreviations are spelled out once in the header rather than per row. */
.labs__legend {
  display: block;
  margin-top: 0.15rem;
  font-weight: 400;
  font-size: 0.6875rem;
  color: #9ca3af;
  white-space: normal;
}
.labs__legend-item::after {
  content: ' · ';
}
.labs__legend-item:last-child::after {
  content: '';
}
/* A grid, not flex-wrap: every row renders the same categories in the same
   order, so fixed columns keep A/C/IH/L/M aligned down the list. */
.labs__chips {
  display: grid;
  gap: 0.3rem;
}
.labs__chip {
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.1rem 0.4rem;
  border: 1px solid #dbeafe;
  border-radius: 999px;
  background: #eff6ff;
  font: inherit;
  font-size: 0.75rem;
  color: #1d4ed8;
  cursor: pointer;
  white-space: nowrap;
}
.labs__chip:hover {
  border-color: #93c5fd;
  background: #dbeafe;
}
.labs__chip-key {
  font-weight: 700;
  letter-spacing: 0.02em;
}
.labs__chip-n {
  font-variant-numeric: tabular-nums;
}
/* Zeros stay visible but recede, so a row of them reads as a gap at a glance. */
.labs__chip--zero {
  background: #f9fafb;
  border-color: #e5e7eb;
  color: #9ca3af;
}
/* Same shape as the other resource pages (see AnalysisTypesView). */
.resource__actions {
  flex-direction: row;
  gap: 0.5rem;
  justify-content: flex-end;
  white-space: nowrap;
}
.labs__empty {
  color: #6b7280;
  padding: 0.75rem 0;
}
.labs__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* Narrow: stack each lab into a card. The five-track grid needs ~46rem before
   the minimums start fighting, so hand over well before that rather than
   letting the columns crush. */
@media (max-width: 60rem) {
  .labs__row--head {
    display: none;
  }
  .labs__row {
    grid-template-columns: 1fr;
    gap: 0.3rem;
    padding: 0.85rem 0.25rem;
  }
  .labs__chips {
    justify-content: start;
  }
  .resource__actions {
    justify-content: flex-start;
    margin-top: 0.2rem;
  }
  .labs__scroll {
    max-height: none;
    overflow: visible;
  }
}
</style>
